import { Button } from "@/components/ui/button";
import { isUnreachableError } from "@/components/atomic-crm/providers/commons/dataError";
import type { ReactNode } from "react";
import { useCallback } from "react";
import type { EditBaseProps, ShowBaseProps } from "ra-core";
import {
  EditBase,
  ShowBase,
  useEditContext,
  useNotify,
  useRedirect,
  useResourceContext,
  useShowContext,
  useTranslate,
} from "ra-core";

type LoadState = {
  error?: unknown;
  isPaused?: boolean;
  record?: unknown;
  refetch?: () => unknown;
};

const showConnectionError = (state: LoadState) =>
  !state.record && (state.isPaused === true || isUnreachableError(state.error));

export const ConnectionError = ({
  onRetry,
  messageKey = "crm.errors.offline",
}: {
  onRetry: () => void;
  messageKey?: string;
}) => {
  const translate = useTranslate();
  return (
    <div
      role="alert"
      className="flex min-h-[40vh] flex-col items-center justify-center gap-4 p-6 text-center"
    >
      <p className="max-w-md text-sm">{translate(messageKey)}</p>
      <Button type="button" onClick={onRetry}>
        {translate("crm.errors.retry")}
      </Button>
    </div>
  );
};

const ConnectionGate = ({
  state,
  children,
}: {
  state: LoadState;
  children?: ReactNode;
}) => {
  if (showConnectionError(state)) {
    return (
      <ConnectionError
        onRetry={() => {
          void state.refetch?.();
        }}
      />
    );
  }
  return children;
};

const ShowConnectionGate = ({ children }: { children?: ReactNode }) => {
  const state = useShowContext();
  return <ConnectionGate state={state}>{children}</ConnectionGate>;
};

const EditConnectionGate = ({ children }: { children?: ReactNode }) => {
  const state = useEditContext();
  return <ConnectionGate state={state}>{children}</ConnectionGate>;
};

const useRecordLoadQueryOptions = (
  queryOptions: ShowBaseProps["queryOptions"],
  redirectOnError: ShowBaseProps["redirectOnError"],
  resourceProp?: string,
) => {
  const resource = useResourceContext({ resource: resourceProp });
  const notify = useNotify();
  const redirect = useRedirect();
  const onError = useCallback(
    (error: Error) => {
      if (isUnreachableError(error)) return;
      if (queryOptions?.onError) {
        queryOptions.onError(error);
        return;
      }
      notify("ra.notification.item_doesnt_exist", { type: "error" });
      if (redirectOnError !== false && resource) {
        redirect(redirectOnError ?? "list", resource);
      }
    },
    [notify, queryOptions, redirect, redirectOnError, resource],
  );
  return { ...queryOptions, onError };
};

/**
 * Show page that stays put when the API cannot be reached.
 * A real missing record still reports "Element bestaat niet" and returns to the list.
 */
export const AppShowBase = ({
  queryOptions,
  redirectOnError,
  children,
  render,
  ...props
}: ShowBaseProps) => {
  const mergedQueryOptions = useRecordLoadQueryOptions(
    queryOptions,
    redirectOnError,
    props.resource,
  );
  return (
    <ShowBase
      {...props}
      redirectOnError={false}
      queryOptions={mergedQueryOptions}
      render={
        render
          ? (controllerProps) => (
              <ConnectionGate state={controllerProps}>
                {render(controllerProps)}
              </ConnectionGate>
            )
          : undefined
      }
    >
      {render ? undefined : <ShowConnectionGate>{children}</ShowConnectionGate>}
    </ShowBase>
  );
};

/** Edit page with the same offline behaviour as AppShowBase. */
export const AppEditBase = ({
  queryOptions,
  redirectOnError,
  children,
  render,
  ...props
}: EditBaseProps) => {
  const mergedQueryOptions = useRecordLoadQueryOptions(
    queryOptions,
    redirectOnError,
    props.resource,
  );
  return (
    <EditBase
      {...props}
      redirectOnError={false}
      queryOptions={mergedQueryOptions}
      render={
        render
          ? (controllerProps) => (
              <ConnectionGate state={controllerProps}>
                {render(controllerProps)}
              </ConnectionGate>
            )
          : undefined
      }
    >
      {render ? undefined : <EditConnectionGate>{children}</EditConnectionGate>}
    </EditBase>
  );
};
